import { CanceledError } from 'axios';
import { AUTH_SESSION_CHANGED } from './sessionEvents';

/** Stable account identity, never a token. Refreshing credentials keeps forms open. */
export function readWorkspaceSessionIdentity(): string {
  try {
    const session = JSON.parse(localStorage.getItem('PLS') || 'null');
    if (!session) return 'anonymous';
    const principal = session.principal ?? session.user ?? session;
    return JSON.stringify([
      session.authMode ?? 'legacy',
      principal.realm ?? 'legacy',
      principal.id ?? null,
      principal.role ?? principal.Role?.name ?? null,
      principal.audience ?? null
    ]);
  } catch {
    return 'invalid';
  }
}

let identity = readWorkspaceSessionIdentity();
let generation = 0;
export function workspaceSessionStamp(): string {
  const current = readWorkspaceSessionIdentity();
  if (current !== identity) {
    identity = current;
    generation += 1;
  }
  return `${generation}:${identity}`;
}
if (typeof window !== 'undefined') {
  window.addEventListener(AUTH_SESSION_CHANGED, workspaceSessionStamp);
}

// Mounted workspaces retain their original account and drafts. A new account
// must open its own workspace, never borrow a still-mounted editor.
const owners = new Map<symbol, string>();
export function retainWorkspaceSession(expected: string): () => void {
  const owner = Symbol('workspace');
  owners.set(owner, expected);
  return () => {
    owners.delete(owner);
  };
}
export const sessionChangedError = () =>
  new CanceledError(
    'Your sign-in changed. Return to the original account to continue in this tab.'
  );
export function assertWorkspaceSession(stamp?: string): string {
  const current = workspaceSessionStamp();
  if (
    (stamp !== undefined && stamp !== current) ||
    [...owners.values()].some(
      (expected) => expected !== readWorkspaceSessionIdentity()
    )
  ) {
    throw sessionChangedError();
  }
  return current;
}
